"use client";

import { useState, useEffect } from "react";
import Swal from "sweetalert2";

const SendDocumentModal = ({
  isOpen,
  onClose,
  onSend,
  transportId,
  vehicles = [],
}) => {
  const [dispatchSheet, setDispatchSheet] = useState(false);
  const [invoice, setInvoice] = useState(false);
  const [vehicleDocs, setVehicleDocs] = useState({});
  const [customerEmail, setCustomerEmail] = useState("");

  useEffect(() => {
    if (isOpen) {
      const init = {};
      vehicles.forEach((v) => {
        init[v.id] = { bol: false, gatepass: false, other: false, photos: false };
      });
      setVehicleDocs(init);
      setDispatchSheet(false);
      setInvoice(false);
      setCustomerEmail("");
    }
  }, [isOpen, vehicles]);

  const toggleVehicleDoc = (vid, field) => {
    setVehicleDocs((prev) => ({
      ...prev,
      [vid]: { ...prev[vid], [field]: !prev[vid][field] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !dispatchSheet &&
      !invoice &&
      !Object.values(vehicleDocs).some((vd) => vd.bol || vd.gatepass || vd.other || vd.photos)
    ) {
      return Swal.fire({
        icon: "warning",
        title: "Nenhum documento selecionado",
        text: "Marque ao menos um documento para enviar.",
      });
    }

    const token = localStorage.getItem("token");
    if (!token) {
      return Swal.fire({
        icon: "error",
        title: "Não autenticado",
        text: "Faça login e tente novamente.",
      });
    }

    const payload = {
      email: customerEmail,
      is_dispatchsheet_marked: dispatchSheet,
      is_invoice_marked: invoice,
      load_items: vehicles.map((v) => ({
        id: v.id,
        is_bol_marked: vehicleDocs[v.id].bol,
        is_gatepass_marked: vehicleDocs[v.id].gatepass,
        is_another_document_marked: vehicleDocs[v.id].other,
        is_photos_marked: vehicleDocs[v.id]?.photos,
      })),
    };

    try {
      const res = await fetch(
        `https://new.cajuu.app/api/loadtransport/send_documents/${transportId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Screen-Name": "/Load",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (res.status === 401) {
        return Swal.fire({
          icon: "error",
          title: "No Permission",
          // text: data.message || "yP.",
        });
      }
      if (res.status === 404) {
        return Swal.fire({
          icon: "error",
          title: "No Document Found",
          // text: data.message || "ID inválido.",
        });
      }
      if (!res.ok) {
        throw new Error(data.message || "Error sending documents");
      }

      Swal.fire({
        icon: "success",
        title: "Sending documents",
        text: data.message,
        showConfirmButton: false,
        timer: 2000,
      });

      onSend?.();
      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error sending documents",
        text: err.message,
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="relative w-11/12 md:w-2/3 lg:w-1/2 bg-[#1c1b19] rounded-xl border border-gray-600 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="px-6 py-4 border-b border-gray-600 relative">
          <h2 className="text-xl font-semibold text-white">
            Send Documents to Customer
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-6 space-y-6">
          {/* EMAIL */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Customer’s Email:
            </label>
            <input
              type="email"
              required
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="user@email.com"
              className="w-full bg-transparent border border-gray-600 rounded-full px-4 py-2 placeholder-gray-500 text-white focus:outline-none"
            />
          </div>

          {/* LOAD DOCUMENTS */}
          <div>
            <h3 className="text-center text-white font-semibold uppercase">
              LOAD DOCUMENTS
            </h3>
            <div className="mt-3 bg-[#292826] p-4 rounded-md flex justify-center gap-12">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={dispatchSheet}
                  onChange={() => setDispatchSheet(!dispatchSheet)}
                  className="form-checkbox h-5 w-5 text-white"
                />
                <span className="text-white">Dispatch Sheet</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={invoice}
                  onChange={() => setInvoice(!invoice)}
                  className="form-checkbox h-5 w-5 text-white"
                />
                <span className="text-white">Invoice</span>
              </label>
            </div>
          </div>

          {/* VEHICLES */}
          <div>
            <div className="mt-3 bg-[#292826] p-4 rounded-md space-y-4">
              {vehicles.map((v, idx) => (
                <div
                  key={v.id}
                  className={
                    idx < vehicles.length - 1
                      ? "border-b border-gray-600 pb-4 mb-4"
                      : ""
                  }
                >
                  <div className="text-center text-white font-semibold uppercase">
                    {v.year} {v.make} {v.model}
                  </div>
                  <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-2">
                    <label className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={vehicleDocs[v.id]?.bol}
                        onChange={() => toggleVehicleDoc(v.id, "bol")}
                        className="form-checkbox h-5 w-5 text-white"
                      />
                      <span className="text-white text-sm">BOL</span>
                    </label>
                    <label className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={vehicleDocs[v.id]?.gatepass}
                        onChange={() => toggleVehicleDoc(v.id, "gatepass")}
                        className="form-checkbox h-5 w-5 text-white"
                      />
                      <span className="text-white text-sm">Gatepass</span>
                    </label>
                    <label className="flex items-center space-x-1">
                       <input
                         type="checkbox"
                         checked={vehicleDocs[v.id]?.photos}
                         onChange={() => toggleVehicleDoc(v.id, "photos")}
                         className="form-checkbox h-5 w-5 text-white"
                       />
                       <span className="text-white text-sm">Inspection Photos</span>
                    </label>
                    <label className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={vehicleDocs[v.id]?.other}
                        onChange={() => toggleVehicleDoc(v.id, "other")}
                        className="form-checkbox h-5 w-5 text-white"
                      />
                      <span className="text-white text-sm">
                        Another Document
                      </span>
                    </label>                    
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-4 px-6 py-4 border-t border-gray-600">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full bg-pink-400 text-white hover:bg-pink-500"
          >
            Close
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-full bg-white text-black hover:bg-gray-100"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default SendDocumentModal;
